import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

interface SidebarCollapseOverrideContextValue {
    /** True when at least one drawer/page requests a temporary collapse. */
    forceCollapsed: boolean;
    acquire: () => void;
    release: () => void;
}

const SidebarCollapseOverrideContext = createContext<SidebarCollapseOverrideContextValue | null>(null);

export const SidebarCollapseOverrideProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    // Refcounted (ENG-2180): several drawers can be open/stacked at once, and a
    // closing drawer must not re-expand the rail while another is still open.
    const [holdCount, setHoldCount] = useState(0);

    const acquire = useCallback(() => setHoldCount((count) => count + 1), []);
    const release = useCallback(() => setHoldCount((count) => Math.max(0, count - 1)), []);

    const value = useMemo(
        () => ({
            forceCollapsed: holdCount > 0,
            acquire,
            release,
        }),
        [holdCount, acquire, release],
    );

    return (
        <SidebarCollapseOverrideContext.Provider value={value}>
            {children}
        </SidebarCollapseOverrideContext.Provider>
    );
};

/** Read whether the left SidebarMenu should be force-collapsed (does not mutate user preference). */
export const useSidebarForceCollapsed = (): boolean => {
    return useContext(SidebarCollapseOverrideContext)?.forceCollapsed ?? false;
};

/**
 * While `force` is true, collapses the app SidebarMenu if it is expanded (ENG-2180:
 * every drawer collapses the rail to maximize content width). Releases on unmount /
 * when `force` becomes false so the user's saved preference is restored.
 */
export const useForceSidebarCollapsed = (force: boolean): void => {
    const ctx = useContext(SidebarCollapseOverrideContext);
    const acquire = ctx?.acquire;
    const release = ctx?.release;

    useEffect(() => {
        if (!force || !acquire || !release) return;
        acquire();
        return release;
    }, [force, acquire, release]);
};

export default SidebarCollapseOverrideContext;
